// We are disabling deprecation rule in order to ensure the component still works for retrocompatibility
import { Children, type ReactNode } from 'react';

import { iterateXYChartAnnotations } from './iterate-xy-chart-annotations.js';
import { Intent, IntentProps } from '../../../core/slots/Intent/Intent.js';
import { isSlot } from '../../../core/utils/is-slot.js';
import { XYChartAnnotationsConfigProps } from '../context/XYChartAnnotationsConfig.context.js';
import { Annotations } from '../slots/Annotations/Annotations.js';
import { DotSeries } from '../slots/DotSeries.js';
import { Legend } from '../slots/Legend.js';
import { RectSeries } from '../slots/RectSeries.js';
import { Threshold } from '../slots/Threshold.js';
import { Toolbar } from '../slots/Toolbar.js';
import { XAxis } from '../slots/XAxis.js';
import { YAxis } from '../slots/YAxis.js';
import type { XYChartSlots } from '../types/xy-chart-internals.js';

/**
 * Extracts XY chart's slots
 */
export const iterateSlots = (children: ReactNode): XYChartSlots => {
  let slots: XYChartSlots = {
    rectSeries: [],
    dotSeries: [],
    yAxes: [],
    xAxes: [],
    legend: {},
    toolbar: {},
    thresholds: [],
  };

  let intents: IntentProps[] | undefined;

  Children.forEach(children, (child) => {
    if (isSlot(child, RectSeries)) {
      slots.rectSeries = [...slots.rectSeries, child.props];
    } else if (isSlot(child, DotSeries)) {
      slots.dotSeries = [...slots.dotSeries, child.props];
    } else if (isSlot(child, XAxis)) {
      slots.xAxes = [...slots.xAxes, child.props];
    } else if (isSlot(child, YAxis)) {
      slots.yAxes = [...slots.yAxes, child.props];
    } else if (isSlot(child, Legend)) {
      slots.legend = child.props;
    } else if (isSlot(child, Toolbar)) {
      slots.toolbar = child.props;
    } else if (isSlot(child, Threshold)) {
      slots.thresholds.push(child.props);
    } else if (isSlot(child, Intent)) {
      intents = intents ? [...intents, child.props] : [child.props];
    } else if (isSlot(child, Annotations)) {
      const annotations = iterateXYChartAnnotations(
        child.props as XYChartAnnotationsConfigProps,
      );
      if (annotations) {
        slots.annotations = annotations;
      }
    }
  });

  if (Object.keys(slots.toolbar).length > 0) {
    slots = {
      ...slots,
      toolbar: {
        ...slots.toolbar,
        intents,
      },
    };
  }

  return slots;
};
